// TS twin of iOS `WheelPlacementController.swift`.
//
// Owns the tap-to-place flow, per-wheel adjustment state, and the
// selection/delete/reset surface. Builds against the WheelRenderer
// protocol so iOS RealityKit / Android Three.js wiring is interchangeable.
//
// Closes audit §8 HIGH placement-race blind spot: every refusal branch in
// handleTap fires wheel_placement_refused with a structured `reason`;
// successful placement fires wheel_placed; selection/delete/reset all
// emit their own events. Property keys mirror iOS so PostHog dashboards
// can union both platforms.

import type { WheelRenderer, WheelId, ScreenPoint } from '@/ar/wheel-renderer'
import {
  arSessionState,
  manualPlacementPhase,
  DEFAULT_MANUAL_WHEEL_ADJUSTMENTS,
  type ARSessionState,
  type ManualPlacementPhase,
  type ManualWheelAdjustments,
} from '@/models/placement-state'
import {
  WHEEL_SCALE_IDENTITY,
  wheelScaleFromSizes,
  type WheelScale,
} from '@/models/wheel-scale'
import { resolveBaseDiameter } from '@/models/base-diameter'
import { DEFAULT_ESTIMATED_SIZE_SNAP_RANGE_IN } from '@/models/estimated-size-snap-range'
import { snapLadder, snapPinchScale } from '@/models/wheel-diameter-snap'
import { wheelSize, type WheelSize } from '@/models/wheel-size'
import { atoLog } from '@/services/ato-log'

// ── Loader contract the controller depends on ─────────────────────────

export interface WheelLoaderSnapshot {
  isLoading: boolean
  hasLoadedModel: boolean
  wheelInfo: {
    assembledSize: WheelSize | null
    selectedSize: WheelSize | null
    sizes: readonly WheelSize[]
    /** ADR-104 — `sizes` are the sizes SOLD; the first is not the mesh's own. */
    sizesAreOffered?: boolean
  }
  /** The GLB's own size — the default when the payload has none. */
  glbDiameterInches?: number | null
}

export interface WheelLoaderLike {
  getState(): WheelLoaderSnapshot
}

// ── Public state ──────────────────────────────────────────────────────

export interface WheelPlacementControllerState {
  readonly phase: ManualPlacementPhase
  readonly placedWheelCount: number
  /** Placement order, oldest first. UI renders numbered chips (1)(2)...
   *  and highlights the one whose id matches selectedWheelId. */
  readonly placedWheelIds: readonly WheelId[]
  readonly selectedWheelId: WheelId | null
  /** True between "+" and the tap that lands the wheel. The preview is
   *  on screen, so from the user's side that wheel already exists — the
   *  nav shows its chip rather than another "+", and the trash cancels
   *  the placement instead of deleting anything (2026-08-23). */
  readonly placementPending: boolean
  readonly adjustmentsForSelected: ManualWheelAdjustments
}

export type RefusalReason =
  | 'model_not_loaded'
  | 'max_wheels_reached'
  | 'not_ready'
  | 'not_aiming'
  | 'raycast_miss'

export interface WheelPlacementControllerConfig {
  renderer: WheelRenderer
  loader: WheelLoaderLike
  onToast?: (message: string) => void
  maxWheels?: number
  /** Whether a freshly placed wheel becomes the selected one.
   *
   *  True for the joystick, which has nothing to act on until something
   *  is selected. False under ?touchUi, where selection is a visible
   *  green ring and the user asked to reach for it deliberately —
   *  auto-selecting means every placement arms drag and pinch on a
   *  wheel nobody has touched yet. */
  autoSelectOnPlace?: boolean
  /** ?use_estimated_size_snap_range — the pinch's +/- span, whole inches,
   *  when the size is ESTIMATED from the GLB. Default 3; 0 = no pinch. */
  estimatedSizeSnapRangeInches?: number
}

interface HandleTapInput {
  screenPoint: ScreenPoint
  viewSize: { width: number; height: number }
  isLandscape: boolean
}

export const MAX_CAMBER_ANGLE_RAD = (20 * Math.PI) / 180
// Two: the AR flow is "see these on my car", and a second wheel is
// enough to judge a pair. Past that the add button disappears
// (2026-08-23).
export const DEFAULT_MAX_WHEELS = 2
const PORTRAIT_VERTICAL_OFFSET = 40

type Listener = (state: WheelPlacementControllerState) => void

// ── Controller ────────────────────────────────────────────────────────

export class WheelPlacementController {
  private state: WheelPlacementControllerState = {
    phase: manualPlacementPhase.idle(),
    placedWheelCount: 0,
    placedWheelIds: [],
    selectedWheelId: null,
    placementPending: false,
    adjustmentsForSelected: { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS },
  }
  private readonly listeners = new Set<Listener>()

  private readonly renderer: WheelRenderer
  private readonly loader: WheelLoaderLike
  private readonly onToast: ((message: string) => void) | undefined
  private readonly estimatedSizeSnapRangeInches: number
  private readonly maxWheels: number
  private autoSelectOnPlace: boolean

  private adjustments = new Map<WheelId, ManualWheelAdjustments>()

  /** Pinch-snap bookkeeping (2026-09-07). `continuous` is the raw
   *  accumulated pinch multiplier — kept so slow pinches add up across
   *  moves even while the APPLIED multiplier holds a whole-inch step.
   *  `applied` records the multiplier this path last wrote; when the
   *  wheel's live multiplier differs (the joystick's continuous scale
   *  wrote it), the accumulator re-seeds from the live value instead of
   *  snapping from stale state. */
  private pinchSnap = new Map<WheelId, { continuous: number; applied: number }>()

  constructor(config: WheelPlacementControllerConfig) {
    this.renderer = config.renderer
    this.loader = config.loader
    this.onToast = config.onToast
    this.estimatedSizeSnapRangeInches =
      config.estimatedSizeSnapRangeInches ?? DEFAULT_ESTIMATED_SIZE_SNAP_RANGE_IN
    this.maxWheels = config.maxWheels ?? DEFAULT_MAX_WHEELS
    this.autoSelectOnPlace = config.autoSelectOnPlace ?? true
  }

  getState(): WheelPlacementControllerState {
    return this.state
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  // ── Tap flow ─────────────────────────────────────────────────────────

  handleTap(input: HandleTapInput): void {
    // 1. Tap-on-wheel → select.
    const hitWheelId = this.renderer.hitTestWheel(input.screenPoint)
    if (hitWheelId != null) {
      this.selectWheel(hitWheelId)
      return
    }

    // 2. Tap-on-empty when something is selected → deselectAll.
    if (this.state.selectedWheelId != null) {
      this.deselectAll()
      return
    }

    // 3. Pre-place gates — each surfaces a structured refusal.
    const loaderState = this.loader.getState()
    if (loaderState.isLoading || !loaderState.hasLoadedModel) {
      this.refuse('model_not_loaded', 'Still loading…')
      return
    }
    if (this.state.placedWheelCount >= this.maxWheels) {
      // Silent at the UI layer; telemetry still records it.
      this.refuse('max_wheels_reached', null)
      return
    }
    if (!isReadyForPlacement(this.renderer.getSessionState())) {
      this.refuse(
        'not_ready',
        'Move your phone slowly to scan the surface',
      )
      return
    }

    // 3.5. Aiming gate (2026-08-21): with a wheel placed and aiming
    // not re-armed (no "+" pressed), a bare tap must NOT drop another
    // wheel — silent refusal, telemetry only.
    if (!(this.renderer.isAiming?.() ?? true)) {
      this.refuse('not_aiming', null)
      return
    }

    // 4. Raycast at the view center (NOT the tap point — deterministic UX).
    const centerY =
      input.viewSize.height / 2 -
      (input.isLandscape ? 0 : PORTRAIT_VERTICAL_OFFSET)
    const centerPoint: ScreenPoint = {
      x: input.viewSize.width / 2,
      y: centerY,
    }
    const surfaceHit = this.renderer.hitTest(centerPoint)
    if (surfaceHit == null) {
      this.update({ phase: manualPlacementPhase.idle() })
      this.refuse('raycast_miss', 'Target wheel not found, please try again')
      return
    }

    // 5. Place.
    this.placeAtCenter(surfaceHit, loaderState)
  }

  /** Places a new wheel at the current view-center hit. Used by the
   *  "+" (add wheel) button in the multi-wheel manager — same gates
   *  as handleTap's placement branch, without the tap-on-wheel /
   *  tap-on-empty selection logic. Returns true on success. */
  addWheelAtCameraTarget(input: HandleTapInput): boolean {
    const loaderState = this.loader.getState()
    if (loaderState.isLoading || !loaderState.hasLoadedModel) {
      this.refuse('model_not_loaded', 'Still loading…')
      return false
    }
    if (this.state.placedWheelCount >= this.maxWheels) {
      this.refuse('max_wheels_reached', null)
      return false
    }
    if (!isReadyForPlacement(this.renderer.getSessionState())) {
      this.refuse(
        'not_ready',
        'Move your phone slowly to scan the surface',
      )
      return false
    }
    // 2026-08-21: with an aiming-capable renderer, "+" RE-ARMS the full
    // first-wheel flow (preview + gauge; the NEXT tap places) instead
    // of dropping a wheel at the view center immediately. Legacy
    // renderers keep the immediate-place behaviour below.
    if (this.renderer.beginPlacement != null) {
      this.renderer.beginPlacement()
      // Aiming for a NEW wheel, so nothing should still be selected:
      // the green ring belongs to the wheel you are about to place, and
      // a stale selection leaves drag and pinch armed on the previous
      // one while the user is lining up the next (2026-08-23 LUAT).
      this.deselectAll()
      // Notify subscribers so the React overlay re-renders and the aim
      // hint reappears — beginPlacement itself changes no controller
      // state, and without a notify the chip stays hidden for wheel 2.
      this.update({ phase: manualPlacementPhase.idle(), placementPending: true })
      return true
    }
    const centerY =
      input.viewSize.height / 2 -
      (input.isLandscape ? 0 : PORTRAIT_VERTICAL_OFFSET)
    const centerPoint: ScreenPoint = {
      x: input.viewSize.width / 2,
      y: centerY,
    }
    const surfaceHit = this.renderer.hitTest(centerPoint)
    if (surfaceHit == null) {
      this.refuse('raycast_miss', 'Target wheel not found, please try again')
      return false
    }
    this.placeAtCenter(surfaceHit, loaderState)
    return true
  }

  /** ?use_ios_ui (2026-10-17) — the Place button. handleTap's placement
   *  gates and centre raycast, without its select/deselect branches, the
   *  aiming gate (the iOS UI never arms the 3D preview) or auto-select
   *  (the iOS flow lands in View, nothing selected). Returns the new id,
   *  or null when a gate refused. */
  placeAtViewCenter(input: HandleTapInput): WheelId | null {
    const loaderState = this.loader.getState()
    if (loaderState.isLoading || !loaderState.hasLoadedModel) {
      this.refuse('model_not_loaded', 'Still loading…')
      return null
    }
    if (this.state.placedWheelCount >= this.maxWheels) {
      this.refuse('max_wheels_reached', null)
      return null
    }
    if (!isReadyForPlacement(this.renderer.getSessionState())) {
      this.refuse('not_ready', 'Move your phone slowly to scan the surface')
      return null
    }
    const surfaceHit = this.renderer.hitTest(viewCenter(input))
    if (surfaceHit == null) {
      this.update({ phase: manualPlacementPhase.idle() })
      this.refuse('raycast_miss', 'Target wheel not found, please try again')
      return null
    }
    const id = this.placeAtCenter(surfaceHit, loaderState, false)
    this.update({ phase: manualPlacementPhase.idle() })
    return id
  }

  private placeAtCenter(
    surfaceHit: ReturnType<WheelRenderer['hitTest']>,
    loaderState: WheelLoaderSnapshot,
    select: boolean = this.autoSelectOnPlace,
  ): WheelId | null {
    if (surfaceHit == null) return null
    this.update({ phase: manualPlacementPhase.placing() })
    const sizeScale = this.deriveSizeScale(loaderState)
    const placed = this.renderer.placeWheel(surfaceHit, sizeScale)

    this.adjustments.set(placed.id, { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS })
    atoLog.event('wheel_placed', {
      wheel_id: placed.id,
      scale_face: sizeScale.face,
      scale_width: sizeScale.width,
    })
    this.update({
      placedWheelCount: this.state.placedWheelCount + 1,
      placedWheelIds: [...this.state.placedWheelIds, placed.id],
      // Landed — the pending chip becomes a real one.
      placementPending: false,
    })
    if (select) this.selectWheel(placed.id)
    return placed.id
  }

  // ── Selection ───────────────────────────────────────────────────────

  /** Abandons an armed placement: the preview goes away and the nav
   *  drops back to (1)(+). Leaves every placed wheel alone. */
  cancelPendingPlacement(): void {
    if (!this.state.placementPending) return
    this.renderer.endPlacement?.()
    atoLog.event('placement_cancelled')
    this.update({
      phase: manualPlacementPhase.idle(),
      placementPending: false,
    })
  }

  /** Runtime toggle — see WheelAdjustMode. Touch mode wants the user to
   *  reach for a wheel deliberately; the arrow pad has nothing to act
   *  on until something is selected. */
  setAutoSelectOnPlace(enabled: boolean): void {
    this.autoSelectOnPlace = enabled
  }

  selectWheel(id: WheelId): void {
    this.renderer.setSelectedWheel(id)
    const adj =
      this.adjustments.get(id) ?? { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS }
    this.update({
      selectedWheelId: id,
      phase: manualPlacementPhase.selected(id),
      adjustmentsForSelected: adj,
    })
    atoLog.event('wheel_selected', { wheel_id: id })
  }

  deselectAll(): void {
    this.renderer.setSelectedWheel(null)
    this.update({
      selectedWheelId: null,
      phase: manualPlacementPhase.idle(),
      adjustmentsForSelected: { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS },
    })
  }

  // ── Adjustments (delta-based; mirror iOS controller's adjustHeight/etc.) ──

  adjustHeight(delta: number): void {
    this.mutateSelected((adj) => ({
      ...adj,
      heightOffset: adj.heightOffset + delta,
    }))
  }

  adjustDepth(delta: number): void {
    // Yaw-aware: local forward = cos(yaw)·normal − sin(yaw)·horizontal.
    this.mutateSelected((adj) => ({
      ...adj,
      depthOffset: adj.depthOffset + delta * Math.cos(adj.yawAngle),
      horizontalOffset: adj.horizontalOffset - delta * Math.sin(adj.yawAngle),
    }))
  }

  adjustHorizontal(delta: number): void {
    // Yaw-aware: local right = cos(yaw)·horizontal + sin(yaw)·normal.
    this.mutateSelected((adj) => ({
      ...adj,
      horizontalOffset: adj.horizontalOffset + delta * Math.cos(adj.yawAngle),
      depthOffset: adj.depthOffset + delta * Math.sin(adj.yawAngle),
    }))
  }

  adjustTilt(delta: number): void {
    this.mutateSelected((adj) => ({
      ...adj,
      tiltAngle: clamp(
        adj.tiltAngle + delta,
        -MAX_CAMBER_ANGLE_RAD,
        MAX_CAMBER_ANGLE_RAD,
      ),
    }))
  }

  adjustYaw(delta: number): void {
    this.mutateSelected((adj) => ({
      ...adj,
      yawAngle: adj.yawAngle + delta,
    }))
  }

  /** Multiplicative scale delta (e.g. +0.05 = grow 5 %). Clamped to a
   *  0.25..4.0 range so a stray double-tap can't zero-out or explode
   *  the wheel. */
  adjustScale(delta: number): void {
    this.mutateSelected((adj) => ({
      ...adj,
      scaleMultiplier: clamp(adj.scaleMultiplier + delta, 0.25, 4),
    }))
  }

  /** Pinch-scale with whole-inch snapping (2026-09-07). The applied
   *  multiplier locks onto whole-inch diameters between 9" and 30",
   *  derived from the payload size; a payload without any size falls
   *  back to the continuous adjustScale behaviour, because there is no
   *  inch to snap to. The pinch path calls this; the joystick's scale
   *  buttons stay on adjustScale. */
  adjustScaleSnapped(delta: number): void {
    const id = this.state.selectedWheelId
    if (id == null) return
    const resolved = this.snapBaseDiameter()
    if (resolved == null) {
      this.adjustScale(delta)
      return
    }
    const base = resolved.inches
    const current = (
      this.adjustments.get(id) ?? DEFAULT_MANUAL_WHEEL_ADJUSTMENTS
    ).scaleMultiplier
    const tracked = this.pinchSnap.get(id)
    const continuous =
      tracked != null && tracked.applied === current
        ? tracked.continuous
        : current
    const snap = snapPinchScale({
      baseDiameterInches: base,
      continuousMultiplier: continuous + delta,
      ladder: snapLadder({
        baseDiameterInches: base,
        sizeDiameters: this.loader.getState().wheelInfo.sizes.map((size) => size.diameterInches),
        // Riley's rules (2026-10-05): a declared range is the ladder; ONE
        // payload size is that size, no pinch (span 0); an ESTIMATED size
        // steps +/- ?use_estimated_size_snap_range.
        halfSpanInches: resolved.source === 'glb' ? this.estimatedSizeSnapRangeInches : 0,
      }),
    })
    if (snap == null) {
      this.adjustScale(delta)
      return
    }
    this.pinchSnap.set(id, {
      continuous: snap.continuousMultiplier,
      applied: snap.snappedMultiplier,
    })
    if (snap.snappedMultiplier !== current) {
      this.mutateSelected((adj) => ({
        ...adj,
        scaleMultiplier: snap.snappedMultiplier,
      }))
    }
  }

  // ── Resize / delete / reset ─────────────────────────────────────────

  applySizeScale(scale: WheelScale): void {
    for (const wheel of this.renderer.getWheels()) {
      this.renderer.setWheelScale(wheel.id, scale)
    }
  }

  deleteSelectedWheel(): void {
    // Mid-placement the trash is an UNDO for the placement itself: the
    // user pressed "+", saw the preview, and changed their mind. There
    // is nothing placed to delete yet, so cancel and go back to the
    // previous nav state rather than removing a wheel they still want.
    if (this.state.placementPending) {
      this.cancelPendingPlacement()
      return
    }
    const id = this.state.selectedWheelId
    if (id == null) return
    this.renderer.removeWheel(id)
    this.adjustments.delete(id)
    this.pinchSnap.delete(id)
    atoLog.event('wheel_deleted', { wheel_id: id })
    const nextIds = this.state.placedWheelIds.filter((wid) => wid !== id)

    // Auto-select the last remaining wheel so the manual-adjustment
    // panel stays visible (and the next tap on the canvas won't be
    // interpreted as a placement gesture — handleTap's "tap on empty
    // while a wheel is selected → deselect" branch fires instead).
    // If nothing remains, drop to idle.
    if (nextIds.length > 0) {
      const nextId = nextIds[nextIds.length - 1]
      const adj =
        this.adjustments.get(nextId) ?? { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS }
      this.renderer.setSelectedWheel(nextId)
      this.update({
        selectedWheelId: nextId,
        placedWheelCount: nextIds.length,
        placedWheelIds: nextIds,
        phase: manualPlacementPhase.selected(nextId),
        adjustmentsForSelected: adj,
      })
      atoLog.event('wheel_selected', { wheel_id: nextId })
      return
    }

    this.update({
      selectedWheelId: null,
      placedWheelCount: 0,
      placedWheelIds: nextIds,
      phase: manualPlacementPhase.idle(),
      adjustmentsForSelected: { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS },
    })
  }

  resetAll(): void {
    const prevCount = this.state.placedWheelCount
    this.renderer.clearAllWheels()
    this.adjustments.clear()
    this.pinchSnap.clear()
    atoLog.event('wheels_reset', { prev_count: prevCount })
    this.update({
      selectedWheelId: null,
      placedWheelCount: 0,
      placedWheelIds: [],
      phase: manualPlacementPhase.idle(),
      adjustmentsForSelected: { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS },
    })
  }

  // ── internal ────────────────────────────────────────────────────────

  private refuse(reason: RefusalReason, toast: string | null): void {
    atoLog.event('wheel_placement_refused', { reason })
    if (toast != null) this.onToast?.(toast)
  }

  private mutateSelected(
    fn: (adj: ManualWheelAdjustments) => ManualWheelAdjustments,
  ): void {
    const id = this.state.selectedWheelId
    if (id == null) return
    const current =
      this.adjustments.get(id) ?? { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS }
    const next = fn(current)
    this.adjustments.set(id, next)
    this.renderer.adjustWheel(id, next)
    this.update({ adjustmentsForSelected: next })
  }

  /** Diameter the wheel RENDERS at before any multiplier: the payload
   *  size, else the GLB's own (resolveBaseDiameterInches). Null when
   *  neither is usable — the pinch then stays continuous. */
  private snapBaseDiameter(): { inches: number; source: 'payload' | 'glb' } | null {
    const state = this.loader.getState()
    return resolveBaseDiameter({
      wheelInfo: state.wheelInfo,
      glbDiameterInches: state.glbDiameterInches,
    })
  }

  private deriveSizeScale(loaderState: WheelLoaderSnapshot): WheelScale {
    // ADR-104 — the reference is what the MESH is: its modeled size, else
    // (for an offered list, whose entries are only sizes sold) the GLB
    // estimate; a modeled list keeps today's first-entry reference.
    const reference =
      loaderState.wheelInfo.assembledSize ??
      (loaderState.wheelInfo.sizesAreOffered
        ? glbReferenceSize(loaderState.glbDiameterInches)
        : loaderState.wheelInfo.sizes[0]) ??
      null
    const selected = loaderState.wheelInfo.selectedSize
    if (reference == null || selected == null) {
      return WHEEL_SCALE_IDENTITY
    }
    return wheelScaleFromSizes(reference, selected)
  }

  private update(patch: Partial<WheelPlacementControllerState>): void {
    this.state = { ...this.state, ...patch }
    for (const listener of Array.from(this.listeners)) listener(this.state)
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

/** The point every centre placement raycasts — handleTap's step 4. */
function viewCenter(input: HandleTapInput): ScreenPoint {
  return {
    x: input.viewSize.width / 2,
    y: input.viewSize.height / 2 - (input.isLandscape ? 0 : PORTRAIT_VERTICAL_OFFSET),
  }
}

/** The GLB estimate as a size, when the renderer measured one. */
function glbReferenceSize(glbDiameterInches: number | null | undefined): WheelSize | null {
  return glbDiameterInches != null && glbDiameterInches > 0 ? wheelSize(String(glbDiameterInches)) : null
}

function isReadyForPlacement(state: ARSessionState): boolean {
  switch (state.kind) {
    case 'ready':
    case 'tracking':
    case 'placing':
    case 'placed':
      return true
    default:
      return false
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

// Re-export so tests can confirm we use the same factory shape (no drift).
export { arSessionState }
